/**
 * Prosumer Calculator - Form Types
 *
 * Form values and validation types for the prosumer wizard.
 * Aligned with backend ProsumerCalculationInput but owned by frontend.
 */

import type { OSDCode } from '../../../utils/osdMetadata';
import { TariffType } from '../../../types/graphql';

// ============================================================================
// ENUMS / LITERALS (frontend-owned, aligned with backend)
// ============================================================================

export type CustomerType = 'HOUSEHOLD' | 'BUSINESS';

export type ConsumptionMethod = 'ANNUAL' | 'MONTHLY' | 'CSV' | 'IMPORTED';

export type SettlementType = 'NET_BILLING' | 'NET_METERING';

export type RCEPricingChoice = 'HOURLY' | 'MONTHLY';

export type RoofOrientation = 'SOUTH' | 'EAST' | 'WEST' | 'FLAT';

export type EVChargingPattern = 'NIGHT' | 'EVENING' | 'WEEKEND' | 'DAYTIME' | 'MIXED';

export type InsulationLevel = 'GOOD' | 'MODERATE' | 'POOR';

export type InstallationCostPreset = 'ECONOMY' | 'STANDARD' | 'PREMIUM' | 'CUSTOM';

export type RCEPriceScenario = 'CONSERVATIVE' | 'EXPECTED' | 'OPTIMISTIC' | 'CUSTOM';

export type HeatPumpType = 'AIR_SOURCE' | 'GROUND_SOURCE';

export type HeatingSystemType = 'UNDERFLOOR' | 'RADIATORS';

export type PolishRegion =
  | 'NORTHEAST'
  | 'CENTRAL'
  | 'SOUTHWEST'
  | 'SOUTHEAST'
  | 'NORTHWEST'
  | 'SOUTH';

// ============================================================================
// DEVICE CONFIG (frontend form values)
// ============================================================================

export interface PVFormConfig {
  capacityKwp: number;
  orientation: RoofOrientation;
  settlementType: SettlementType;
  rcePricingChoice: RCEPricingChoice;
  installationDate: string;
  installationCostPln: number;
  installationCostPreset: InstallationCostPreset;
  rcePriceScenario: RCEPriceScenario;
  rcePriceOverridePln: number | null;
}

export interface EVFormConfig {
  annualMileageKm: number;
  consumptionKwhPer100km: number;
  chargingPattern: EVChargingPattern;
  chargerPowerKw: number;
}

export interface HeatPumpFormConfig {
  houseAreaM2: number;
  insulationLevel: InsulationLevel;
  pumpType: HeatPumpType;
  includesCWU: boolean;
  heatingSystemType: HeatingSystemType;
}

// ============================================================================
// MÓJ PRĄD 7.0 SUBSIDY CONFIG
// ============================================================================

export interface MojPradConfig {
  /** New installation (required for MP 7.0 storage upgrades) */
  isNewInstallation: boolean;
  /** Battery energy storage included */
  hasBatteryStorage: boolean;
  /** Heat energy storage included */
  hasHeatStorage: boolean;
  /** Battery capacity in kWh (0 if no battery) */
  batteryCapacityKwh: number;
  /** Natural person required for subsidy (business customers ineligible) */
  isNaturalPerson: boolean;
}

// ============================================================================
// CSV DATA (from splitCSVByEnergyType)
// ============================================================================

export interface CSVImportData {
  /** Monthly consumption totals from CSV */
  monthlyConsumptionKwh: number[];
  /** Monthly export totals from CSV (prosumer) */
  monthlyExportKwh: number[];
  /** Whether export data exists (prosumer installation) */
  hasProsumerData: boolean;
  /** Raw hourly data for backend calculation */
  hourlyConsumptionData: Array<{
    hour: number;
    dayOfMonth: number;
    month: number;
    consumptionKwh: number;
  }>;
  hourlyExportData: Array<{
    hour: number;
    dayOfMonth: number;
    month: number;
    exportKwh: number;
  }>;
}

// ============================================================================
// MAIN FORM VALUES
// ============================================================================

export interface ProsumerFormValues {
  // Step 1: Profile
  customerType: CustomerType;
  distributor: OSDCode;
  year: number;
  currentTariff: TariffType | '';
  region?: PolishRegion;

  // Step 2: Consumption
  consumptionMethod: ConsumptionMethod;
  annualConsumptionKwh: number;
  monthlyConsumptionKwh: number[];
  csvData: CSVImportData | null;

  // Step 3: Devices
  hasPV: boolean;
  hasEV: boolean;
  hasHeatPump: boolean;
  pvConfig: PVFormConfig;
  evConfig: EVFormConfig;
  heatPumpConfig: HeatPumpFormConfig;

  // Tariffs to compare
  tariffTypes: TariffType[];

  // Mój Prąd 7.0 subsidy eligibility
  mojPradConfig: MojPradConfig;
}

// ============================================================================
// DEFAULTS
// ============================================================================

export const DEFAULT_PV_CONFIG: PVFormConfig = {
  capacityKwp: 5,
  orientation: 'SOUTH',
  settlementType: 'NET_BILLING',
  rcePricingChoice: 'HOURLY',
  installationDate: '2025-01-01',
  installationCostPln: 0,
  installationCostPreset: 'STANDARD',
  rcePriceScenario: 'EXPECTED',
  rcePriceOverridePln: null,
};

export const DEFAULT_MOJ_PRAD_CONFIG: MojPradConfig = {
  isNewInstallation: true,
  hasBatteryStorage: false,
  hasHeatStorage: false,
  batteryCapacityKwh: 0,
  isNaturalPerson: true,
};

export const DEFAULT_EV_CONFIG: EVFormConfig = {
  annualMileageKm: 15000,
  consumptionKwhPer100km: 18,
  chargingPattern: 'NIGHT',
  chargerPowerKw: 7.2,
};

export const DEFAULT_HP_CONFIG: HeatPumpFormConfig = {
  houseAreaM2: 120,
  insulationLevel: 'MODERATE',
  pumpType: 'AIR_SOURCE',
  includesCWU: true,
  heatingSystemType: 'UNDERFLOOR',
};

export const DEFAULT_FORM_VALUES: ProsumerFormValues = {
  // Profile
  customerType: 'HOUSEHOLD',
  distributor: 'TAURON',
  year: 2026,
  currentTariff: '',
  region: 'CENTRAL',

  // Consumption
  consumptionMethod: 'ANNUAL',
  annualConsumptionKwh: 3000,
  monthlyConsumptionKwh: [300, 270, 260, 230, 200, 180, 170, 180, 200, 240, 270, 300],
  csvData: null,

  // Devices
  hasPV: true,
  hasEV: false,
  hasHeatPump: false,
  pvConfig: { ...DEFAULT_PV_CONFIG },
  evConfig: { ...DEFAULT_EV_CONFIG },
  heatPumpConfig: { ...DEFAULT_HP_CONFIG },

  // Tariffs — initialized to all tariffs available for the default distributor (TAURON)
  tariffTypes: [
    TariffType.G11,
    TariffType.G12,
    TariffType.G12w,
    TariffType.G12as,
    TariffType.G13,
    TariffType.G13s,
    TariffType.G14,
  ],

  // Mój Prąd 7.0 config
  mojPradConfig: { ...DEFAULT_MOJ_PRAD_CONFIG },
};

// ============================================================================
// VALIDATION
// ============================================================================

export type ProsumerFormErrors = Partial<Record<string, string>>;

// ============================================================================
// API RESULT TYPES (mirroring backend ProsumerCalculationResult)
// ============================================================================

/** Monthly billing result (net-billing or net-metering) */
export interface MonthlyBillingResult {
  month: number;
  totalBillGrossPln: number;
  depositBalancePln?: number;  // net-billing
  bankBalanceKwh?: number;     // net-metering
  depositCreatedPln?: number;
  depositExpiredPln?: number;
  bankedKwh?: number;
  expiredKwh?: number;
  /** Distribution (sieciowe) charge component */
  distributionChargePln?: number;
  /** Fixed monthly charges (abonamentowe, przesyłowe stałe) */
  fixedChargesPln?: number;
  /** Energy charge after RCE deposit deduction */
  energyChargeAfterDepositPln?: number;
  /** Grid consumption this month (kWh drawn from grid) */
  gridConsumptionKwh?: number;
  /** Energy exported to grid this month (kWh) */
  exportedKwh?: number;
}

export interface ProsumerTariffComparison {
  tariffType: TariffType;
  costWithoutPV: number;
  costWithPV: number;
  savingsPln: number;
  savingsPercent: number;
  /** Detailed billing results from the prosumer engine (nested as sent by Lambda) */
  prosumerResult?: {
    monthlyResults?: MonthlyBillingResult[];
    /** Total RCE deposit created over the year (PLN equivalent) */
    totalDepositCreatedPln?: number;
    /** Total RCE deposit actually redeemed (PLN) */
    totalDepositUsedPln?: number;
    /** RCE deposit that expired unredeemed (PLN) — lost value */
    totalDepositExpiredPln?: number;
    /** Year-end overpayment refund (20–30% of excess deposit, PLN) */
    overpaymentRefundPln?: number;
    /** Annual distribution charges total (PLN) */
    distributionChargePln?: number;
    /** Annual fixed charges total (PLN) */
    fixedChargesPln?: number;
  };
}

export interface ProsumerSelfConsumption {
  totalProductionKwh: number;
  totalSelfConsumptionKwh: number;
  totalExportKwh: number;
  totalGridImportKwh: number;
  effectiveRatio: number;
  monthlyRatios: number[];
  /** Monthly PV production breakdown (12 values, kWh per month) */
  monthlyProductionKwh: number[];
  /** Monthly grid import (12 values, kWh per month) */
  monthlyGridImportKwh: number[];
}

export interface ProsumerROIResult {
  paybackYears: number | null;
  paybackCalendarYear: number | null;
  /** e.g. "Q2 2034" */
  paybackCalendarQuarter: string | null;
  netPresentValue25yr: number | null;
  totalSavings25yr: number;
  /** Internal Rate of Return in percent (e.g. 9.2 = 9.2%) */
  irr: number | null;
  assumedInstallationYear: number;
}

export interface MojPradSubsidyResult {
  eligibleAmount: number;
  programVersion: 'MP_7_0' | 'INELIGIBLE';
  reason?: string;
  details: {
    pvBaseAmount: number;
    storageAdditional: number;
  };
}

export interface ProsumerCalculationResult {
  selfConsumption: ProsumerSelfConsumption;
  tariffComparisons: ProsumerTariffComparison[];
  recommendedTariff: TariffType;
  maxSavingsPln: number;
  maxSavingsPercent: number;
  /** ROI analysis (only present if installationCostPln > 0) */
  roi?: ProsumerROIResult;
  /** Mój Prąd 7.0 subsidy estimate */
  subsidyEstimate?: MojPradSubsidyResult;
  /** Warnings from the calculation (e.g. partial-year CSV data) */
  warnings?: string[];
}

// Re-export TariffType for convenience within the module
export { TariffType };
